const CONSENT_STORAGE_KEY = 'agency.privacy.consent.v1';
const REQUEST_STORAGE_KEY = 'agency.privacy.requests.v1';

/** @typedef {{ id: string, type: 'export' | 'delete', status: string, email: string, reason: string, requestedAt: string }} DataRequest */
/** @typedef {Window & { __AGENCY_PRIVACY__?: Record<string, unknown> }} PrivacyWindow */

const DEFAULT_CONSENT = {
  necessary: true,
  analytics: false,
  errorTracking: false,
  marketing: false,
  decidedAt: null
};

function getStorage() {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.localStorage;
}

export function getConsentState() {
  const storage = getStorage();
  if (!storage) {
    return { ...DEFAULT_CONSENT };
  }

  const raw = storage.getItem(CONSENT_STORAGE_KEY);
  if (!raw) {
    return { ...DEFAULT_CONSENT };
  }

  try {
    const parsed = JSON.parse(raw);
    return {
      ...DEFAULT_CONSENT,
      ...parsed
    };
  } catch {
    return { ...DEFAULT_CONSENT };
  }
}

/** @param {Partial<typeof DEFAULT_CONSENT>} patch */
export function setConsentState(patch) {
  const storage = getStorage();
  const next = {
    ...getConsentState(),
    ...patch,
    decidedAt: new Date().toISOString()
  };

  if (storage) {
    storage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(next));
  }

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('agency:privacy-consent-changed', { detail: next }));
  }

  return next;
}

/** @param {'necessary' | 'analytics' | 'errorTracking' | 'marketing'} category */
export function hasPrivacyConsent(category) {
  const state = getConsentState();
  if (category === 'necessary') {
    return true;
  }

  if (category === 'analytics') {
    return Boolean(state.analytics);
  }

  if (category === 'errorTracking') {
    return Boolean(state.errorTracking);
  }

  if (category === 'marketing') {
    return Boolean(state.marketing);
  }

  return false;
}

function getPrivacyRequests() {
  const storage = getStorage();
  if (!storage) {
    return [];
  }

  const raw = storage.getItem(REQUEST_STORAGE_KEY);
  if (!raw) {
    return [];
  }

  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/** @param {DataRequest[]} requests */
function savePrivacyRequests(requests) {
  const storage = getStorage();
  if (!storage) {
    return;
  }

  storage.setItem(REQUEST_STORAGE_KEY, JSON.stringify(requests));
}

/** @param {'export' | 'delete'} type @param {{ email: string, reason?: string }} payload */
export function submitDataRequest(type, payload) {
  const request = {
    id: `privacy_${Date.now()}`,
    type,
    status: 'queued',
    email: payload.email,
    reason: payload.reason || '',
    requestedAt: new Date().toISOString()
  };

  const requests = [...getPrivacyRequests(), request];
  savePrivacyRequests(requests);
  return request;
}

export function listDataRequests() {
  return getPrivacyRequests();
}

export function initializePrivacyCompliance() {
  const state = getConsentState();

  if (typeof window !== 'undefined') {
    const typedWindow = /** @type {PrivacyWindow} */ (window);

    typedWindow.__AGENCY_PRIVACY__ = {
      getConsentState,
      setConsentState,
      hasPrivacyConsent,
      submitDataRequest,
      listDataRequests
    };

    if (!state.decidedAt) {
      window.dispatchEvent(new CustomEvent('agency:privacy-consent-required'));
    }
  }

  return state;
}
